import React, { useEffect, useState, useCallback } from "react";
import PageTableLayout from "../../components/Pagetablelayout";
import axios from "axios";
import {
  Space,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Row,
  Col,
  message,
} from "antd";

export default function PackagesPage() {
  const [packages, setPackages] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [form] = Form.useForm();

  const token = localStorage.getItem("jwt");
  // Match TagsPage: configurable base URL instead of a hardcoded domain
  const API_BASE = process.env.REACT_APP_API_URL || "https://mdgholidaydreams.com/api";

  // Match TagsPage: shared helper, and always send Content-Type on writes
  const headersWithAuth = (extra = {}) =>
    token ? { Authorization: `Bearer ${token}`, ...extra } : { ...extra };

  const fetchPackages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/packages/`, {
        headers: headersWithAuth(),
      });
      const data = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
      setPackages(data);
    } catch (err) {
      console.error("Fetch packages failed:", err.response?.data || err.message);
      message.error("Failed to load packages");
    } finally {
      setLoading(false);
    }
  }, [token]); // token used indirectly via headersWithAuth

  const fetchCountries = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/countries/`, {
        headers: headersWithAuth(),
      });
      const data = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
      setCountries(data);
    } catch (err) {
      console.error("Fetch countries failed:", err.response?.data || err.message);
      message.error("Failed to load countries");
    }
  }, [token]);

  const fetchCities = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/cities/`, {
        headers: headersWithAuth(),
      });
      const data = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
      setCities(data);
    } catch (err) {
      console.error("Fetch cities failed:", err.response?.data || err.message);
      message.error("Failed to load cities");
    }
  }, [token]);

  const fetchTags = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/tags/`, {
        headers: headersWithAuth(),
      });
      const data = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
      setTags(data);
    } catch (err) {
      console.error("Fetch tags failed:", err.response?.data || err.message);
      message.error("Failed to load tags");
    }
  }, [token]);

  useEffect(() => {
    // run once on mount; functions are stable because of useCallback
    fetchPackages();
    fetchCountries();
    fetchCities();
    fetchTags();
  }, [fetchPackages, fetchCountries, fetchCities, fetchTags]);

  const handleAdd = () => {
    setEditingPackage(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const safeParseTags = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [raw];
      } catch {
        // fallback: comma separated string
        return raw.split(",").map((t) => t.trim()).filter(Boolean);
      }
    }
    return [];
  };

  const handleEdit = (record) => {
    setEditingPackage(record);
    form.setFieldsValue({
      title: record.title,
      description: record.description,
      duration: record.duration,
      price: record.price,
      image: record.image,
      included: record.included,
      tags: safeParseTags(record.tags),
      country_id: record.country_id,
      city_id: record.city_id,
      featured: Number(record.featured) === 1 || record.featured === true,
    });
    setIsModalVisible(true);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      // ensure tags are sent as array (backend expects JSON-encoded array)
      const payload = {
        ...values,
        tags: values.tags || [],
        featured: values.featured ? 1 : 0,
      };

      console.log("Package payload being sent:", payload);

      if (editingPackage) {
        // Trailing slash before the query string, same fix applied on TagsPage,
        // to avoid a redirect that would otherwise silently drop the PUT body
        await axios.put(
          `${API_BASE}/packages/?id=${editingPackage.id}`,
          payload,
          { headers: headersWithAuth({ "Content-Type": "application/json" }) }
        );
        message.success("Package updated");
      } else {
        await axios.post(`${API_BASE}/packages/`, payload, {
          headers: headersWithAuth({ "Content-Type": "application/json" }),
        });
        message.success("Package created");
      }

      form.resetFields();
      setIsModalVisible(false);
      setEditingPackage(null);
      fetchPackages();
    } catch (err) {
      console.error("Save package failed:", err.response?.data || err.message);
      message.error(err.response?.data?.message || "Failed to save package");
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingPackage(null);
    form.resetFields();
  };

  const selectedCountryId = Form.useWatch("country_id", form);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE}/packages/?id=${id}`, {
        headers: headersWithAuth(),
      });
      message.success("Package deleted");
      fetchPackages();
    } catch (err) {
      console.error("Delete failed:", err.response?.data || err.message);
      message.error("Failed to delete package");
    }
  };

  const handleToggleActive = async (id) => {
    try {
      // Fixed: the backend's PATCH handler lives on /packages itself
      // (id read from the query string), not a separate /activate route
      await axios.patch(
        `${API_BASE}/packages/?id=${id}`,
        {},
        { headers: headersWithAuth() }
      );
      fetchPackages();
    } catch (err) {
      console.error("Toggle active failed:", err.response?.data || err.message);
      message.error("Failed to toggle package status");
    }
  };

  const columns = [
    { title: "Title", dataIndex: "title", key: "title" },
    { title: "Price", dataIndex: "price", key: "price" },
    { title: "Duration", dataIndex: "duration", key: "duration" },
    {
      title: "Image Link",
      dataIndex: "image",
      key: "image",
      render: (url) => (url ? <a href={url} target="_blank" rel="noopener noreferrer">{url}</a> : "—"),
    },
    {
      title: "Featured",
      dataIndex: "featured",
      key: "featured",
      render: (val) => (Number(val) === 1 || val === true ? "Yes" : "No"),
    },
    {
      title: "Active",
      dataIndex: "active",
      key: "active",
      render: (active) => (Number(active) === 1 || active === true ? "Active" : "Inactive"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button onClick={() => handleEdit(record)}>Edit</Button>
          <Button danger onClick={() => handleDelete(record.id)}>Delete</Button>
          <Button onClick={() => handleToggleActive(record.id)}>
            {Number(record.active) === 1 || record.active === true ? "Deactivate" : "Activate"}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageTableLayout
        breadcrumb={["Admin", "Packages"]}
        title="Manage Packages"
        addButtonLabel="Add Package"
        onAdd={handleAdd}
        columns={columns}
        dataSource={packages}
        loading={loading}
        onRefresh={fetchPackages}
      />

      <Modal
        title={editingPackage ? "Edit Package" : "Add New Package"}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText="Save"
        width={800}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="title" label="Title" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="description" label="Description" rules={[{ required: true }]}>
                <Input.TextArea rows={3} />
              </Form.Item>
              <Form.Item name="duration" label="Duration" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="price" label="Price (USD)" rules={[{ required: true }]}>
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item name="image" label="Image Link (https://...)" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="tags" label="Tags" rules={[{ required: true }]}>
                <Select mode="multiple" placeholder="Select tags" showSearch optionFilterProp="children">
                  {tags.map((tag) => (
                    <Select.Option key={tag.id} value={tag.name}>
                      {tag.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item name="country_id" label="Country" rules={[{ required: true }]}>
                <Select
                  showSearch
                  placeholder="Select country"
                  optionFilterProp="children"
                  onChange={() => form.setFieldsValue({ city_id: undefined })}
                >
                  {countries.map((c) => (
                    <Select.Option key={c.id} value={c.id}>
                      {c.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item name="city_id" label="City" rules={[{ required: true }]}>
                <Select showSearch placeholder="Select city" optionFilterProp="children">
                  {cities
                    .filter((city) => Number(city.country_id) === Number(selectedCountryId))
                    .map((city) => (
                      <Select.Option key={city.id} value={city.id}>
                        {city.name}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>

              <Form.Item name="included" label="What’s Included" rules={[{ required: true }]}>
                <Input.TextArea rows={3} placeholder="e.g. Flights, Accommodation, Meals" />
              </Form.Item>

              <Form.Item name="featured" label="Featured Package" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}